import TextArea from '../../../../molecules/TextArea';
import ToogleSwitch from '../../../../atoms/ToogleSwitch';
import SwitchField from './SwitchField';
import SelectTextField from '../../../../molecules/SelectTextField';
import CollectionOrderTable from './CollectionOrderTable';
import AgreementPartiesSummary from './AgreementPartiesSummary';
import type { FieldError } from '../../../../../services/collectionApi';
import { CollectionAgreement } from '../collectionAgreementModel';
import { canGenerateBoleto, withGenerateBoleto, withPolicy } from '../agreementAccountRules';
import { ACCOUNT_KIND_LABEL, CollectionPolicy } from '../collectionModel';

interface CollectionAgreementCardProps {
    agreement: CollectionAgreement;
    policies: CollectionPolicy[];
    fieldErrors: FieldError[];
    notice?: React.ReactNode;
    onChange: (agreement: CollectionAgreement) => void;
}

const CardHeader = ({ agreement, onChange }: { agreement: CollectionAgreement; onChange: (agreement: CollectionAgreement) => void }) => (
    <header className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 px-5 py-4 dark:border-slate-700">
        <div className="flex items-center gap-3">
            <ToogleSwitch checked={agreement.collect} ariaLabel="Cobrar" onChange={collect => onChange({ ...agreement, collect })} />
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Cobrança</h3>
        </div>
        <div className="flex flex-wrap items-center gap-6 text-sm">
            <span className="text-gray-600 dark:text-gray-300">
                Conta <strong className="text-gray-800 dark:text-gray-100">{ACCOUNT_KIND_LABEL[agreement.receivingAccountKind]}</strong>
            </span>
            {canGenerateBoleto(agreement.receivingAccountKind)
                ? <SwitchField label="Gerar boleto" checked={agreement.payment.instruction === 'BOLETO'}
                    onChange={generate => onChange(withGenerateBoleto(agreement, generate))} />
                : <span className="text-gray-500">Escrow não gera boleto</span>}
        </div>
    </header>
);

const policyItems = (policies: CollectionPolicy[], currentId: string) => policies
    .filter(policy => policy.status === 'ACTIVE' || policy.id === currentId)
    .map(policy => ({ value: policy.id, label: policy.status === 'ACTIVE' ? policy.name : `${policy.name} (Desativada)` }));

const CollectionAgreementCard = ({ agreement, policies, fieldErrors, notice, onChange }: CollectionAgreementCardProps) => {
    const policy = policies.find(p => p.id === agreement.policyId);
    return (
        <section className="rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
            <CardHeader agreement={agreement} onChange={onChange} />
            <div className="space-y-6 px-5 py-5">
                {notice}
                {agreement.collect ? (
                    <>
                        <div className="grid grid-cols-1 items-end gap-x-6 gap-y-2 pt-4 md:grid-cols-[minmax(260px,1fr)_2fr]">
                            <SelectTextField id="agreement-policyId" label="Política de cobrança" items={policyItems(policies, agreement.policyId)}
                                value={agreement.policyId} emptyLabel="Escolha a política"
                                errorMessage={fieldErrors.find(e => e.fieldName === 'policyId')?.message}
                                onChange={e => onChange(withPolicy(agreement, policies.find(p => p.id === e.target.value)))} />
                            {policy?.description && <p className="pb-2 text-sm text-gray-600 dark:text-gray-300">{policy.description}</p>}
                        </div>
                        {policy && <CollectionOrderTable agreement={agreement} policy={policy} onChange={onChange} />}
                        {fieldErrors.filter(e => e.fieldName.startsWith('collectionOrder')).map(e => (
                            <p key={e.fieldName} className="text-sm text-red-600">{e.message}</p>
                        ))}
                    </>
                ) : <p className="text-sm text-gray-500">Esta dívida não é cobrada.</p>}
                <TextArea id="agreement-notes" label="Exceções / Observações" rows={3} value={agreement.notes ?? ''}
                    errorMessage={fieldErrors.find(e => e.fieldName === 'notes')?.message}
                    onChange={e => onChange({ ...agreement, notes: e.target.value })} />
                <AgreementPartiesSummary parties={agreement.parties} />
            </div>
        </section>
    );
};

export default CollectionAgreementCard;
